import { FC, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

import {
  getDeal,
  getJobContentByJobId,
  getJobsByDealId,
  getUserRoles,
} from '@/utils/stateAccessors';

import { JobActionType } from '@payaca/types/jobActionsTypes';

import { isInvoice } from '@payaca/helpers/jobStatusHelper';

import { actions as jobActions } from '@/api/jobs';

import { useSelector } from '@/api/state';
import { Button, Modal } from '@payaca/ambrosia';
import IconButton from '@payaca/components/button/IconButton';
import ResponsiveViewWrapper from '@payaca/components/responsiveViewWrapper/ResponsiveViewWrapper';
import TitledContentPanel from '@payaca/components/titledContentPanel/TitledContentPanel';
import { canAddProposalToDeal } from '@payaca/helpers/dealHelper';
import { PaymentsPermissions } from '@payaca/permissions/payments/payments.permissions';
import { userHasRequiredPermission } from '@payaca/permissions/permissions.utils';
import DealListedJobsTable, {
  QuickActionDefinitions,
} from '../dealListedJobsTable/DealListedJobsTable';
import RecordPaymentModal from '../recordPaymentModal/RecordPaymentModal';
import ResendJobModal from '../resendJobModal/ResendJobModal';
import RestoreJobModal from '../restoreJobModal/RestoreJobModal';
import './DealProposalsInvoices.sass';

interface Props {
  dealId: number;
  onDealUpdateSuccess: (updatedJobIds?: number[]) => void;
}

export const DealProposalsInvoices: FC<Props> = ({
  dealId,
  onDealUpdateSuccess,
}: Props) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [isArchivingJob, setIsArchivingJob] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiveJobId, setArchiveJobId] = useState<number>();
  const [isMarkingJobAccepted, setIsMarkingJobAccepted] = useState(false);
  const [isMarkingJobDeclined, setIsMarkingJobDeclined] = useState(false);
  const [isRaisingInvoice, setIsRaisingInvoice] = useState(false);
  const [resendJobId, setResendJobId] = useState<number>();
  const [recordPaymentJobId, setRecordPaymentJobId] = useState<number>();
  const [restoreJobId, setRestoreJobId] = useState<number>();
  const userRoles = useSelector(getUserRoles);

  const deal = useSelector((state) => {
    return getDeal(state, dealId);
  });
  const jobs = useSelector((state) => {
    return getJobsByDealId(state, dealId);
  });
  const isGettingJobsForDeal = useSelector((state) => {
    return state.jobsStore.isGettingJobsForDeal;
  });

  const recordPaymentJobContent = useSelector((state) => {
    if (!recordPaymentJobId) return;
    return getJobContentByJobId(state, recordPaymentJobId);
  });

  const proposals = useMemo(() => {
    return jobs?.filter((x) => !isInvoice(x.status));
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: jobs.length
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: jobs.length
  }, [jobs, jobs?.length]);

  const invoiceableProposalId = useMemo(() => {
    return proposals.find(
      (x) => !x.archivedAt && !x.inactivatedAt && !isInvoice(x.status)
    )?.id;
  }, [proposals]);

  const invoices = useMemo(() => {
    return jobs?.filter((x) => isInvoice(x.status));
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: jobs.length
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: jobs.length
  }, [jobs, jobs?.length]);

  const markJobAccepted = useCallback(
    (jobId: number) => {
      setIsMarkingJobAccepted(true);
      dispatch(
        jobActions.acceptQuote(jobId, () => {
          onDealUpdateSuccess([jobId]);
          setIsMarkingJobAccepted(false);
        })
      );
    },
    [dispatch, onDealUpdateSuccess]
  );

  const markJobDeclined = useCallback(
    (jobId: number) => {
      setIsMarkingJobDeclined(true);
      dispatch(
        jobActions.declineJob(jobId, () => {
          onDealUpdateSuccess([jobId]);
          setIsMarkingJobDeclined(false);
        })
      );
    },
    [dispatch, onDealUpdateSuccess]
  );

  const invoiceJob = useCallback(
    (jobId: number) => {
      setIsRaisingInvoice(true);
      dispatch(
        jobActions.convertJobToInvoice(jobId, (err: Error, resp: any) => {
          setIsRaisingInvoice(false);
          if (!err) {
            history.push({
              pathname: `/invoices/${resp.invoiceId}`,
            });
          }
        })
      );
    },
    [dispatch, history]
  );

  const archiveJob = useCallback((jobId: number) => {
    setArchiveJobId(jobId);
    setShowArchiveModal(true);
  }, []);

  const confirmArchiveJob = useCallback(() => {
    if (!archiveJobId) return;
    setIsArchivingJob(true);
    dispatch(
      jobActions.archiveJob(archiveJobId, () => {
        setIsArchivingJob(false);
        setShowArchiveModal(false);
        setArchiveJobId(undefined);
        onDealUpdateSuccess(jobs.map((x) => x.id));
      })
    );
  }, [dispatch, archiveJobId, jobs, onDealUpdateSuccess]);

  const goToSendJob = useCallback(
    (jobId: number) => {
      const job = jobs.find((x) => x.id === jobId);
      if (!job) return;

      const jobIsInvoice = isInvoice(job.status);

      history.push(
        `/${jobIsInvoice ? 'invoices' : 'quotes'}/${jobId}?view=send`
      );
    },
    [jobs, history]
  );

  const quickActionDefinitions = useMemo(() => {
    let qa: QuickActionDefinitions = {
      [JobActionType.MARK_AS_ACCEPTED]: {
        actionName: 'Mark as accepted',
        actionBehaviour: markJobAccepted,
        isActionProcessing: isMarkingJobAccepted,
      },
      [JobActionType.MARK_AS_DECLINED]: {
        actionName: 'Mark as declined',
        actionBehaviour: markJobDeclined,
        isActionProcessing: isMarkingJobDeclined,
      },
      [JobActionType.INVOICE]: {
        actionName: 'Invoice',
        actionBehaviour: invoiceJob,
        isActionProcessing: isRaisingInvoice,
      },
      [JobActionType.ARCHIVE]: {
        actionName: 'Archive',
        actionBehaviour: archiveJob,
        isActionProcessing: isArchivingJob,
      },
      [JobActionType.SEND]: {
        actionName: 'Send',
        actionBehaviour: goToSendJob,
      },
      [JobActionType.RESEND]: {
        actionName: 'Resend',
        actionBehaviour: setResendJobId,
      },
      [JobActionType.UNARCHIVE]: {
        actionName: 'Restore',
        actionBehaviour: setRestoreJobId,
      },
    };
    if (
      userHasRequiredPermission(userRoles, [PaymentsPermissions.RECORD_PAYMENT])
    ) {
      qa = {
        ...qa,
        [JobActionType.RECORD_DEPOSIT_PAYMENT]: {
          actionName: 'Record payment',
          actionBehaviour: setRecordPaymentJobId,
        },
        [JobActionType.RECORD_PAYMENT]: {
          actionName: 'Record payment',
          actionBehaviour: setRecordPaymentJobId,
        },
      };
    }
    return qa;
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'userRoles'
  }, [
    markJobAccepted,
    isMarkingJobAccepted,
    markJobDeclined,
    isMarkingJobDeclined,
    invoiceJob,
    isRaisingInvoice,
    archiveJob,
    isArchivingJob,
    goToSendJob,
  ]);

  const sectionTitle = useCallback(
    (
      title: string,
      canAdd: boolean,
      onAddNew: () => void,
      isEmpty: boolean
    ) => {
      return (
        <div
          className={`deal-overview-section-title${
            isEmpty && canAdd ? ' clickable' : ''
          }`}
          onClick={() => canAdd && onAddNew()}
        >
          {/* allow title clicking if empty content */}
          <h3 onClick={(e) => !isEmpty && e.stopPropagation()}>{title}</h3>
          <IconButton icon="plus.3" size="xs" isDisabled={!canAdd} />
        </div>
      );
    },
    []
  );

  const canAddProposal = useMemo(() => {
    return canAddProposalToDeal(deal) && !isGettingJobsForDeal && !jobs?.length;
  }, [deal, isGettingJobsForDeal, jobs?.length]);

  const canAddInvoice = useMemo(() => {
    return (
      !deal?.archivedAt &&
      !isGettingJobsForDeal &&
      (!!(invoiceableProposalId && !invoices?.length) || !jobs?.length)
    );
  }, [
    deal,
    invoiceableProposalId,
    invoices?.length,
    isGettingJobsForDeal,
    jobs?.length,
  ]);

  return (
    <ResponsiveViewWrapper
      downBreakpointSm={900}
      downBreakpointXs={600}
      className="deal-proposals-invoices v1"
    >
      <div className="deal-proposals-invoices-inner">
        {/* Proposals section */}
        <TitledContentPanel
          title={sectionTitle(
            'Proposals',
            canAddProposal,
            () => history.push(`/quotes/new?dealId=${dealId}`),
            !proposals?.length
          )}
          className={!proposals?.length ? 'empty-content' : ''}
        >
          {proposals?.length ? (
            <DealListedJobsTable
              dealId={dealId}
              jobs={proposals}
              quickActionDefinitions={quickActionDefinitions}
            />
          ) : (
            <></>
          )}
        </TitledContentPanel>
        {/* Invoices section */}
        <TitledContentPanel
          title={sectionTitle(
            'Invoices',
            canAddInvoice,
            () => {
              if (!jobs?.length) {
                history.push(`/invoices/new?dealId=${dealId}`);
              } else if (!isRaisingInvoice && invoiceableProposalId) {
                invoiceJob(invoiceableProposalId);
              }
            },
            !invoices?.length
          )}
          className={!invoices?.length ? 'empty-content' : ''}
        >
          {invoices?.length ? (
            <DealListedJobsTable
              dealId={dealId}
              jobs={invoices}
              quickActionDefinitions={quickActionDefinitions}
            />
          ) : (
            <></>
          )}
        </TitledContentPanel>

        {resendJobId && (
          <ResendJobModal
            isOpen={!!resendJobId}
            onClose={() => {
              setResendJobId(undefined);
              // refresh
              onDealUpdateSuccess([resendJobId]);
            }}
            jobId={resendJobId}
          />
        )}
        {recordPaymentJobId && !!recordPaymentJobContent && (
          <RecordPaymentModal
            dealId={dealId}
            paymentTarget={{
              jobId: recordPaymentJobId,
            }}
            isOpen={!!recordPaymentJobId}
            onClose={() => setRecordPaymentJobId(undefined)}
            recordPaymentCallback={() =>
              onDealUpdateSuccess([recordPaymentJobId])
            }
            confirmPaymentCallback={() =>
              onDealUpdateSuccess([recordPaymentJobId])
            }
          />
        )}

        {restoreJobId && (
          <RestoreJobModal
            isOpen={!!restoreJobId}
            jobId={restoreJobId}
            onClose={() => setRestoreJobId(undefined)}
            onRestoreJobCallback={() => {
              setRestoreJobId(undefined);
              onDealUpdateSuccess(jobs.map((x) => x.id));
            }}
          />
        )}

        <Modal
          isOpen={showArchiveModal}
          title="Archive job"
          onClose={() => {
            setShowArchiveModal(false);
            setArchiveJobId(undefined);
          }}
          size="sm"
        >
          <Modal.Body>
            Archiving this job will also archive any related quotes or invoices.
            Are you sure you want to continue?
          </Modal.Body>
          <Modal.Footer>
            <Modal.Footer.Actions>
              <Button
                variant="outline"
                onClick={() => {
                  setShowArchiveModal(false);
                  setArchiveJobId(undefined);
                }}
              >
                Cancel
              </Button>
              <Button onClick={confirmArchiveJob}>Archive</Button>
            </Modal.Footer.Actions>
          </Modal.Footer>
        </Modal>
      </div>
    </ResponsiveViewWrapper>
  );
};
